import { Check, ChevronDown, EyeOff, MessageChatCircle } from "@untitledui/icons";
import type { ReactNode } from "react";
import { GROUP_ICONS } from "@/components/safety-config/risk-groups";
import { cx } from "@/utils/cx";
import type { AdminRiskGroup } from "./risk-grouping";

type RiskGroupCardProps = {
  group: AdminRiskGroup;
  activeCount: number;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
};

export const RiskGroupCard = ({ group, activeCount, isOpen, onToggle, children }: RiskGroupCardProps) => {
  const total = group.risks.length;
  const Icon = GROUP_ICONS[group.id] ?? MessageChatCircle;

  return (
    <div className="rounded-md border border-secondary overflow-hidden">
      <button
        type="button"
        data-testid={`risk-group-${group.id}`}
        className="w-full flex items-center gap-4 px-6 py-5 text-left hover:bg-secondary transition-colors"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <Icon aria-hidden="true" className="w-5 h-5 text-secondary shrink-0" />
        <span className="flex-1 text-sm font-medium text-primary">{group.name}</span>
        {activeCount > 0 ? (
          <span className="text-sm font-medium flex items-center gap-2 text-success-primary">
            {activeCount} of {total} Risks Active
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-success-secondary shrink-0">
              <Check aria-hidden="true" className="w-3.5 h-3.5 text-success-primary" />
            </span>
          </span>
        ) : (
          <span className="text-sm font-medium flex items-center gap-2 text-tertiary">
            {total === 0 ? "No risks yet" : `${total} Risks Disabled`}
            <EyeOff aria-hidden="true" className="w-5 h-5 text-quaternary shrink-0" />
          </span>
        )}
        <ChevronDown
          aria-hidden="true"
          className={cx("w-6 h-6 text-quaternary transition-transform duration-150 shrink-0", isOpen && "rotate-180")}
        />
      </button>
      {isOpen && <div className="border-t border-secondary">{children}</div>}
    </div>
  );
};
